﻿import React, { useState, useEffect, useRef } from 'react'
import { XMarkIcon, UserIcon, EnvelopeIcon, PencilIcon, CalendarIcon, EyeIcon, EyeSlashIcon, CameraIcon } from '@heroicons/react/24/outline'
import { Button } from './ui'
import { useAuthContext } from '../contexts/AuthContext'
import { PasswordStrengthMeter } from './PasswordStrengthMeter'
import { ALLOWED_IMAGE_TYPES, MAX_AVATAR_SIZE_BYTES, getSignedAvatarUrl, uploadAvatar, removeAvatarFile } from '../services/avatar'

 

interface UserConfigModalProps {
  isOpen: boolean
  onClose: () => void
}

const UserConfigModal: React.FC<UserConfigModalProps> = ({ isOpen, onClose }) => {
  const { user, profile, updateProfile, updatePassword } = useAuthContext()
  const [formData, setFormData] = useState({
    name: '',
    birth_date: '',
    email: '',
    phone: ''
  })
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [avatarLoading, setAvatarLoading] = useState(false)
  const [avatarMessage, setAvatarMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  })
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isPasswordLoading, setIsPasswordLoading] = useState(false)
  const [initialFormData, setInitialFormData] = useState<{ name: string; birth_date: string; email: string; phone: string } | null>(null)
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null)
  const avatarInputRef = useRef<HTMLInputElement | null>(null)

  const isDirty = initialFormData
    ? (initialFormData.name !== formData.name || initialFormData.birth_date !== formData.birth_date || initialFormData.phone !== formData.phone)
    : false

  // Carregar dados do usuário quando o modal abrir
  useEffect(() => {
    if (isOpen && user) {
      const birthDate = profile?.birth_date?.slice(0, 10) || ''
      setFormData({
        name: profile?.name || '',
        birth_date: birthDate,
        email: user.email || '',
        phone: profile?.phone || ''
      })
      setInitialFormData({
        name: profile?.name || '',
        birth_date: birthDate,
        email: user.email || '',
        phone: profile?.phone || ''
      })
      previouslyFocusedElementRef.current = (document.activeElement as HTMLElement) || null
      setTimeout(() => {
        const firstFocusable = dialogRef.current?.querySelector<HTMLElement>('input:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])')
        firstFocusable?.focus()
      }, 0)
    }
  }, [isOpen, user, profile])

  // Carregar preview do avatar atual (URL assinada)
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (!isOpen) return
      if (!profile?.avatar_url) {
        setAvatarPreview(null)
        return
      }
      const url = await getSignedAvatarUrl(profile.avatar_url)
      if (!cancelled) setAvatarPreview(url)
    }
    load()
    return () => { cancelled = true }
  }, [isOpen, profile?.avatar_url])

  useEffect(() => {
    if (!isOpen) return
    const body = document.body
    const previousOverflow = body.style.overflow
    body.style.overflow = 'hidden'
    return () => {
      body.style.overflow = previousOverflow
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (!isLoading && !isPasswordLoading) {
          handleClose()
        }
      }
      if (e.key === 'Tab') {
        const focusable = dialogRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), textarea, input:not([disabled]), select, [tabindex]:not([tabindex="-1"])')
        if (!focusable || focusable.length === 0) return
        const focusableArray = Array.from(focusable)
        const first = focusableArray[0]
        const last = focusableArray[focusableArray.length - 1]
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault()
            last.focus()
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault()
            first.focus()
          }
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, isLoading, isPasswordLoading])

  const handleOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isLoading && !isPasswordLoading) {
      handleClose()
    }
  }

  const handleClose = () => {
    previouslyFocusedElementRef.current?.focus()
    onClose()
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAvatarMessage(null)
    const file = e.target.files?.[0]
    if (!file) return

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setAvatarMessage({ type: 'error', text: 'Tipo inválido. Use JPEG ou PNG.' })
      setAvatarFile(null)
      return
    }
    if (file.size > MAX_AVATAR_SIZE_BYTES) {
      setAvatarMessage({ type: 'error', text: 'Arquivo muito grande. Máx: 15MB.' })
      setAvatarFile(null)
      return
    }

    setAvatarFile(file)
    const localUrl = URL.createObjectURL(file)
    setAvatarPreview(localUrl)
  }

  const handleSaveAvatar = async () => {
    if (!user || !avatarFile) return
    setAvatarMessage(null)
    setAvatarLoading(true)
    try {
      const result = await uploadAvatar({
        file: avatarFile,
        userId: user.id,
        oldPath: profile?.avatar_url
      })

      // Sincronizar estado local chamando updateProfile (garante atualização do contexto)
      await updateProfile({ avatar_url: result.path })

      // Regerar URL assinada a partir do path salvo
      const signed = await getSignedAvatarUrl(result.path)
      setAvatarPreview(signed)

      setAvatarMessage({ type: 'success', text: 'Foto atualizada com sucesso!' })
      setAvatarFile(null)
    } catch (err) {
      setAvatarMessage({ type: 'error', text: (err as Error).message || 'Falha ao atualizar foto.' })
    } finally {
      setAvatarLoading(false)
    }
  }

  const handleRemoveAvatar = async () => {
    if (!user) return
    setAvatarMessage(null)
    setAvatarLoading(true)
    try {
      const pathToRemove = profile?.avatar_url ?? null
      await updateProfile({ avatar_url: null })
      await removeAvatarFile(pathToRemove)
      setAvatarPreview(null)
      setAvatarFile(null)
      setAvatarMessage({ type: 'success', text: 'Foto removida com sucesso!' })
    } catch (err) {
      setAvatarMessage({ type: 'error', text: (err as Error).message || 'Falha ao remover foto.' })
    } finally {
      setAvatarLoading(false)
    }
  }

  const handlePasswordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handlePasswordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
    }
  }

  const handleChangePassword = async () => {
    setMessage(null)
    
    if (!passwordData.newPassword || !passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Preencha a nova senha e a confirmação.' })
      return
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'A senha deve ter pelo menos 6 caracteres.' })
      return
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'As senhas não coincidem.' })
      return
    }

    try {
      setIsPasswordLoading(true)
      const result = await updatePassword(passwordData.newPassword)

      if (result.success) {
        setMessage({ type: 'success', text: 'Senha atualizada com sucesso!' })
        setPasswordData({ newPassword: '', confirmPassword: '' })
        setShowNewPassword(false)
        setShowConfirmPassword(false)
      } else {
        setMessage({ type: 'error', text: result.error || 'Erro ao atualizar senha. Tente novamente.' })
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erro inesperado ao atualizar senha.' })
    } finally {
      setIsPasswordLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setMessage(null)

    const birthDate = formData.birth_date.trim()
    if (birthDate && birthDate > new Date().toISOString().slice(0, 10)) {
      setMessage({ type: 'error', text: 'A data de nascimento não pode ser futura.' })
      setIsLoading(false)
      return
    }

    try {
      const result = await updateProfile({
        name: formData.name,
        birth_date: birthDate || null,
        phone: formData.phone
      })

      if (!result.success) {
        setMessage({ type: 'error', text: result.error || 'Erro ao atualizar perfil. Tente novamente.' })
        return
      }
      
      setMessage({ type: 'success', text: 'Perfil atualizado com sucesso!' })
      
      // Fechar modal após 2 segundos
      setTimeout(() => {
        onClose()
      }, 2000)
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error)
      setMessage({ type: 'error', text: 'Erro ao atualizar perfil. Tente novamente.' })
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onMouseDown={handleOverlayMouseDown}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="user-config-title" aria-describedby="user-config-description" className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto focus:outline-none">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10 bg-white dark:bg-gray-800">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-purple-600 rounded-full overflow-hidden flex items-center justify-center">
              {avatarPreview ? (
                <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <UserIcon className="h-5 w-5 text-white" />
              )}
            </div>
            <div>
              <h2 id="user-config-title" className="text-lg font-semibold text-gray-900 dark:text-white">
                Configurações do Usuário
              </h2>
              <p id="user-config-description" className="text-sm text-gray-500 dark:text-gray-400">
                Edite suas informações pessoais
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            aria-label="Fechar configurações do usuário"
            disabled={isLoading || isPasswordLoading}
          >
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Avatar */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Foto do Perfil
            </label>
            <div className="flex items-center space-x-4">
              {avatarPreview ? (
                <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                  <img src={avatarPreview} alt="Prévia do avatar" className="w-full h-full object-cover" />
                </div>
              ) : (
                <label
                  htmlFor="avatar-input"
                  className="w-20 h-20 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border-2 border-dashed border-gray-300 dark:border-gray-600"
                >
                  <CameraIcon className="h-8 w-8 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                  <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 text-center px-1 mt-0.5">Adicione sua foto</span>
                </label>
              )}
              <div className="flex-1">
                <input
                  ref={avatarInputRef}
                  id="avatar-input"
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handleAvatarChange}
                  className="sr-only"
                  aria-label="Selecionar imagem do avatar"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Formatos: JPEG/PNG. Tamanho máximo: 15MB.
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    onClick={handleSaveAvatar}
                    disabled={!avatarFile || avatarLoading}
                    aria-busy={avatarLoading}
                  >
                    {avatarLoading ? 'Salvando...' : (avatarFile ? 'Salvar foto' : 'Selecionar arquivo...')}
                  </Button>
                  {avatarPreview && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={handleRemoveAvatar}
                      disabled={avatarLoading}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20"
                    >
                      Remover foto
                    </Button>
                  )}
                </div>
              </div>
            </div>
            {avatarMessage && (
              <div role="status" className={`mt-3 p-3 rounded-lg text-sm ${
                avatarMessage.type === 'success'
                  ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                  : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
              }`}>
                {avatarMessage.text}
              </div>
            )}
          </div>

          {/* Nome */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Nome Completo
            </label>
            <div className="relative">
              <PencilIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="Digite seu nome completo"
                required
              />
            </div>
          </div>

          {/* Data de nascimento */}
          <div>
            <label htmlFor="birth_date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Data de nascimento
            </label>
            <div className="relative">
              <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                type="date"
                id="birth_date"
                name="birth_date"
                value={formData.birth_date}
                onChange={handleInputChange}
                max={new Date().toISOString().slice(0, 10)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white dark:[color-scheme:dark]"
              />
            </div>
          </div>

          {/* Código Usuário SCGP (somente leitura) */}
          <div>
            <label htmlFor="id_scgp" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Código Usuário SCGP
            </label>
            <input
              type="text"
              id="id_scgp"
              name="id_scgp"
              value={profile?.id_scgp != null ? String(profile.id_scgp) : ''}
              disabled
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed font-mono"
              placeholder="Não informado"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              O código SCGP é definido pelo administrador e não pode ser alterado aqui
            </p>
          </div>

          {/* E-mail (somente leitura) */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email
            </label>
            <div className="relative">
              <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                disabled
                className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              O email não pode ser alterado
            </p>
          </div>

          {/* Telefone */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Telefone
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="(11) 99999-9999"
            />
          </div>

          {/* Alterar Senha */}
          <div className="pt-4 mt-2 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Alterar Senha</h3>
            <div className="space-y-3">
              {/* Nova Senha */}
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nova Senha
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    id="newPassword"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordInputChange}
                    onKeyDown={handlePasswordKeyDown}
                    className="w-full pr-10 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Digite a nova senha"
                    minLength={6}
                    disabled={isPasswordLoading}
                    aria-describedby="password-help"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(v => !v)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    tabIndex={-1}
                    aria-label={showNewPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showNewPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
                <p id="password-help" className="text-xs text-gray-500 dark:text-gray-400 mt-1">Mínimo de 6 caracteres.</p>
                <PasswordStrengthMeter password={passwordData.newPassword} />
              </div>

              {/* Confirmar Senha */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Confirmar Nova Senha
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordInputChange}
                    onKeyDown={handlePasswordKeyDown}
                    className="w-full pr-10 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Confirme a nova senha"
                    minLength={6}
                    disabled={isPasswordLoading}
                    aria-invalid={Boolean(passwordData.confirmPassword) && passwordData.newPassword !== passwordData.confirmPassword}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(v => !v)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    tabIndex={-1}
                    aria-label={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {Boolean(passwordData.confirmPassword) && passwordData.newPassword !== passwordData.confirmPassword && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">As senhas não coincidem.</p>
                )}
              </div>

              <div>
                <Button
                  type="button"
                  onClick={handleChangePassword}
                  className="w-full"
                  disabled={
                    isPasswordLoading ||
                    !passwordData.newPassword ||
                    !passwordData.confirmPassword ||
                    passwordData.newPassword.length < 6 ||
                    passwordData.newPassword !== passwordData.confirmPassword
                  }
                  aria-busy={isPasswordLoading}
                >
                  {isPasswordLoading ? 'Atualizando...' : 'Atualizar Senha'}
                </Button>
              </div>
            </div>
          </div>


          {/* Message */}
          {message && (
            <div role="status" aria-live="polite" className={`p-3 rounded-lg text-sm ${
              message.type === 'success' 
                ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400' 
                : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
            }`}>
              {message.text}
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              className="flex-1"
              disabled={isLoading || isPasswordLoading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={isLoading || !isDirty}
              aria-busy={isLoading}
            >
              {isLoading ? 'Salvando...' : (isDirty ? 'Salvar Alterações' : 'Nada a salvar')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default UserConfigModal
